import React, { useState, useEffect } from "react"
import {
  TAalterApplicationState,
  TAuploadUserPayload,
  TAinitUpload,
  TAsignPart,
  TAcompleteUpload,
  TAabortUpload,
} from "../services/applicationAPI"
import { TAfindCampaignById } from "../services/campaignsAPI"
import { applicationStateLabels } from "../pages/Campaign/findCampaign/FindCampaign"
import {
  generateAvailableStates,
  generateStatePayloads,
  handleStatePayload,
} from "../static/Application"
import { ApplicationDoc } from "../types/applicationData"
import axios, { AxiosProgressEvent } from "axios"

const checkBarter = async (campaign_id: string) => {
  try {
    const campaign = await TAfindCampaignById(campaign_id)
    if (campaign) {
      return campaign.barter
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to get campaign", error)
  }
}

export const UpdateApplicationModal = ({
  isOpen,
  onClose,
  _id,
  campaign_id,
  state,
  application,
}: {
  isOpen: boolean
  onClose: any
  _id: string
  campaign_id: string
  state: string
  application: ApplicationDoc
}) => {
  const [error, setError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [availableStates, setAvailableStates] = useState([""])
  const [newState, setNewState] = useState("")
  const [newStatePayload, setNewStatePayload] = useState("")
  const [view, setView] = useState("")
  const [reelsLikes, setReelsLikes] = useState("")
  const [reelsComments, setReelsComments] = useState("")
  const [reelsInteractions, setReelsInteractions] = useState("")
  const [reelsSavings, setReelsSavings] = useState("")
  const [reelsSharings, setReelsSharings] = useState("")
  const [selectedStatePayloads, setSelectedStatePayloads] = useState("")
  const [selectedSocialMedia, setSelectedSocialMedia] = useState("")
  const [imageLoading, setImageLoading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [address, setAddress] = useState({
    contactName: "",
    contactPhone: "",
    city: "",
    country: "",
    address: "",
    details: "",
    zipCode: "",
    id: "",
    extra_information: "",
  })
  const [user_id, setUser_id] = useState("")
  const [statePayloadTypes, setStatePayloadTypes] = useState([""])

  useEffect(() => {
    const fetchCampaign = async () => {
      const barter = await checkBarter(campaign_id)
      if (barter === true) {
        generateAvailableStates(state, true, setAvailableStates)
      } else {
        generateAvailableStates(state, false, setAvailableStates)
      }
    }
    fetchCampaign()
  }, [state, campaign_id])

  useEffect(() => {
    generateStatePayloads(newState, setStatePayloadTypes)
  }, [newState])

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

  const uploadFileInChunks = async (
    file: File,
    type: string,
    onProgress: (p: number) => void,
  ): Promise<string> => {
    // 1) INIT
    const customFileName = `content_to_share/${campaign_id}/${_id}`
    const init = await TAinitUpload(customFileName, type, file.type)
    if (init.status !== 200 || !init.data.success) {
      throw new Error("init failed")
    }
    const uploadId: string = init.data.uploadId
    const key: string = init.data.key

    // 2) Parçaları hazırla
    const CHUNK_SIZE = 16 * 1024 * 1024
    const totalParts = Math.ceil(file.size / CHUNK_SIZE)

    // Progress durumunu parçaya göre takip edeceğiz
    const partLoaded = new Map<number, number>() // bytes
    let committedBytes = 0 // tamamlanan part'ların toplamı

    const uploadedParts: { PartNumber: number; ETag: string }[] = []

    // Progress hesaplayıcı
    const updateProgress = () => {
      let inFlight = 0
      for (const v of partLoaded.values()) {
        inFlight += v
      }
      const overall = Math.round(((committedBytes + inFlight) * 100) / file.size)
      onProgress(Math.min(100, overall))
    }

    // Tek partı yükleyen yardımcı (retry'li)
    const uploadOnePart = async (partNumber: number) => {
      const start = (partNumber - 1) * CHUNK_SIZE
      const end = Math.min(start + CHUNK_SIZE, file.size)
      const blob = file.slice(start, end)

      // 2a) signed URL
      const sign = await TAsignPart(uploadId, key, partNumber)
      if (sign.status !== 200 || !sign.data.success) {
        throw new Error(`sign fail p${partNumber}`)
      }
      const putUrl: string = sign.data.url

      // 2b) PUT + retry
      const maxAttempts = 3
      let attempt = 0
      let etag: string | null = null

      while (attempt < maxAttempts) {
        try {
          const resp = await axios.put(putUrl, blob, {
            headers: { "Content-Type": "application/octet-stream" },
            withCredentials: false,
            onUploadProgress: (pe: AxiosProgressEvent) => {
              partLoaded.set(partNumber, Math.min(pe.loaded, end - start))
              updateProgress()
            },
            maxBodyLength: Infinity,
          })
          etag =
            (resp.headers?.etag || resp.headers?.ETag || resp.headers?.ETAG)?.replace(/"/g, "") ||
            null
          if (!etag) {
            throw new Error("missing ETag")
          }
          break
        } catch (e) {
          attempt++
          if (attempt >= maxAttempts) {
            throw e
          }
          await sleep(500 * attempt * attempt)
        }
      }

      const partData = { PartNumber: partNumber, ETag: etag! }
      uploadedParts.push(partData)
      const partBytes = end - start
      committedBytes += partBytes
      partLoaded.delete(partNumber)
      updateProgress()
    }

    const CONCURRENCY = 6
    const queue: number[] = Array.from({ length: totalParts }, (_, i) => i + 1)

    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (queue.length) {
        const partNumber = queue.shift()!
        await uploadOnePart(partNumber)
      }
    })

    try {
      await Promise.all(workers)
    } catch (err) {
      try {
        await TAabortUpload(uploadId, key)
      } catch {
        // eslint-disable-next-line no-console
        console.error("Failed to abort upload", err)
      }
      throw err
    }

    uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber)

    // Validate parts before sending
    if (uploadedParts.length === 0) {
      throw new Error("No parts uploaded")
    }

    // Check for invalid parts
    for (const part of uploadedParts) {
      if (!part.PartNumber || part.PartNumber < 1 || !part.ETag || part.ETag.trim() === "") {
        throw new Error(`Invalid part data: ${JSON.stringify(part)}`)
      }
    }

    let complete
    try {
      complete = await TAcompleteUpload(uploadId, key, uploadedParts)
      if (complete.status !== 200 || !complete.data.success) {
        try {
          await TAabortUpload(uploadId, key)
        } catch {
          // eslint-disable-next-line no-console
          console.error("Failed to abort upload", error)
        }
        throw new Error(`Complete failed: ${complete.data.message || "Unknown error"}`)
      }
    } catch (error) {
      try {
        await TAabortUpload(uploadId, key)
      } catch {
        // eslint-disable-next-line no-console
        console.error("Failed to abort upload", error)
      }
      throw error
    }

    onProgress(100)
    return complete.data.url
  }

  const handleSelectPhotoFromPC = async (type: string) => {
    try {
      const input = document.createElement("input")
      input.type = "file"
      input.accept = "*"
      input.onchange = async (e) => {
        if (!(e.target instanceof HTMLInputElement) || !e.target.files) {
          return
        }
        const file = e.target.files[0]

        // Use chunked upload for files larger than 200MB
        const useChunkedUpload = file.size > 200 * 1024 * 1024

        try {
          setImageLoading(true)
          setUploadProgress(0)

          let response
          if (useChunkedUpload) {
            const s3Url = await uploadFileInChunks(file, type, (progress) => {
              setUploadProgress(progress)
            })
            response = { status: 200, data: { s3_url: s3Url } }
          } else {
            // Use original upload for smaller files
            const formData = new FormData()
            formData.append("user_payload", file)
            response = await TAuploadUserPayload(_id, formData, type, (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              setUploadProgress(progress)
            })
          }

          if (response.status !== 200) {
            throw new Error("Failed to upload file")
          }
          setImageLoading(false)
          setUploadProgress(0)
          setNewStatePayload(response.data.s3_url)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error uploading file:", error)
          setImageLoading(false)
          setUploadProgress(0)
        }
      }
      input.click()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error selecting photo:", error)
    }
  }

  const emptyBlankHandler = () => {
    if (newState === "") {
      setError(true)
      return false
    }
    setError(false)
    return true
  }

  const handleNewStateChange = () => {
    let statePayload = {}
    switch (selectedStatePayloads) {
      case "link":
        statePayload = { _id: user_id, media_url: newStatePayload }
        return statePayload
      case "img":
        if (selectedSocialMedia === "tiktok") {
          statePayload = { img: newStatePayload, view: view }
        } else if (selectedSocialMedia === "instagram") {
          statePayload = {
            img: newStatePayload,
            view: view,
            reelslikes: reelsLikes,
            reelscomments: reelsComments,
            reelsinteractions: reelsInteractions,
            reelssavings: reelsSavings,
            reelssharings: reelsSharings,
          }
        } else {
          statePayload = { img: newStatePayload }
        }
        return statePayload
      case "review":
        statePayload = { review: newStatePayload }
        return statePayload
      case "impressions":
        statePayload = { impressions: newStatePayload }
        return statePayload
    }
  }

  const handleSubmitForm = async (e: any) => {
    e.preventDefault()
    if (!emptyBlankHandler()) {
      setIsLoading(false)
      return
    }
    const state_payload = handleNewStateChange()
    setIsLoading(true)
    address.contactPhone = `+${address.contactPhone}`

    try {
      const applicationData = {
        ...application,
        state_payload: typeof state_payload === "string" ? state_payload : undefined,
        state_history: application.state_history,
        state: state,
        address: address,
        last_action_at: new Date(),
      }
      const payload =
        typeof state_payload === "string" ? { payload: state_payload } : state_payload || {}
      await TAalterApplicationState(applicationData, newState, payload)
      setIsLoading(false)
      onClose()
    } catch (error) {
      setIsLoading(false)
      // eslint-disable-next-line no-console
      console.error("Failed to update application", error)
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div
      className="fixed inset-0 bg-gray-900 bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-8 rounded-lg shadow-xl overflow-y-auto overscroll-contain max-h-[90vh] max-w-md w-full space-y-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {imageLoading ? (
          <div className="flex flex-col items-center justify-center h-40 space-y-4">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
            <div className="text-center">
              <p className="text-gray-600 mb-2">Dosya yükleniyor...</p>
              <div className="w-64 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-pink-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500 mt-1">{uploadProgress}%</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Başvuruyu Güncelle</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmitForm} className="space-y-4">
              <label className="block">
                <span className="text-gray-700">Yeni Durumu Seç</span>
                <select
                  className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 mb-8 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  value={newState}
                  onChange={(e) => setNewState(e.target.value)} // ✅
                >
                  <option value="">Durum Seç</option>
                  {availableStates.map((s) => {
                    const stateLabel =
                      applicationStateLabels[s as keyof typeof applicationStateLabels] ?? s
                    return (
                      <option key={s} value={s}>
                        {stateLabel}
                      </option>
                    )
                  })}
                </select>
              </label>

              {statePayloadTypes && (
                <label className="block">
                  <div>
                    <span className="text-gray-700">
                      Durum Yanıt Tipini Seç<span> (opsiyonel)</span>
                    </span>
                    <select
                      className="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 mb-3 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                      value={selectedStatePayloads}
                      onChange={(e) => setSelectedStatePayloads(e.target.value)}
                    >
                      <option value="">Yanıt Tipi Seç</option>
                      {statePayloadTypes.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedStatePayloads &&
                    handleStatePayload(
                      selectedStatePayloads,
                      newState,
                      newStatePayload,
                      user_id,
                      setUser_id,
                      setNewStatePayload,
                      handleSelectPhotoFromPC,
                      setSelectedSocialMedia,
                      selectedSocialMedia,
                      view,
                      setView,
                      reelsLikes,
                      setReelsLikes,
                      reelsComments,
                      setReelsComments,
                      reelsInteractions,
                      setReelsInteractions,
                      reelsSavings,
                      setReelsSavings,
                      reelsSharings,
                      setReelsSharings,
                      address,
                      setAddress,
                    )}
                </label>
              )}

              {error && <p className="text-red-500">Please fill out all fields</p>}
              <div className="flex justify-end space-x-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 rounded"
                >
                  {isLoading ? "Yükleniyor..." : "Kaydet"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded"
                >
                  Kapat
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
